import type { EpcInvitationKind } from './epcInvitationKind';

/** Sell-side stages. The company responds to the client's invitation. */
export const EOI_WORKFLOW_STAGES = [
  'Register client EOI',
  'Internal review',
  'Qualification package',
  'Submit to client',
  'Client evaluation',
  'Shortlist outcome',
  'Continue or close',
] as const;

export const RFQ_WORKFLOW_STAGES = [
  'Register client RFQ',
  'Scope and pricing basis',
  'Approval to quote',
  'Clarifications',
  'Estimate',
  'Submit quotation',
  'Negotiation',
  'Award outcome',
  'Contract',
] as const;

export const RFT_WORKFLOW_STAGES = [
  'Tender strategy',
  'Scope and criteria',
  'Committee and documents',
  'Register invitation',
  'Pre-bid, site visit and Q&A',
  'Technical proposal',
  'Commercial proposal',
  'Clarifications and BAFO',
  'Award',
  'Contract and mobilisation',
] as const;

export type EpcInvitationStage =
  | (typeof EOI_WORKFLOW_STAGES)[number]
  | (typeof RFQ_WORKFLOW_STAGES)[number]
  | (typeof RFT_WORKFLOW_STAGES)[number];

export type EpcQualificationDossier = {
  certificates: string;
  experience: string;
  capacity: string;
  financial: string;
  hse: string;
  iso: string;
  localCompliance: string;
  criteriaFrozen: boolean;
};

export type EpcOwnershipDossier = {
  requestor: string;
  commercialOwner: string;
  technicalReviewer: string;
  approver: string;
};

export type EpcCommercialTermsDossier = {
  paymentTerms: string;
  incoterms: string;
  freight: string;
  vat: string;
  warranty: string;
};

export type EpcRftDossier = {
  particulars: string;
  sow: string;
  boq: string;
  technicalSpecification: string;
  qualification: string;
  commercialConditions: string;
  contractConditions: string;
  evaluationMethodology: string;
  bidInstructions: string;
  experienceWeight: number | null;
  methodologyWeight: number | null;
  resourcesWeight: number | null;
  hseWeight: number | null;
  deliveryWeight: number | null;
  qualificationThreshold: number | null;
  commercialMethod: string;
  commercialLockedUntilTechnical: boolean;
};

export type EpcInvitationDossier = {
  stage: string;
  purpose: string;
  businessUnit: string;
  contractCategory: string;
  qualification: EpcQualificationDossier;
  ownership: EpcOwnershipDossier;
  commercialTerms: EpcCommercialTermsDossier;
  rft: EpcRftDossier;
};

export const CONTRACT_CATEGORY_OPTIONS = [
  'Lump sum',
  'Unit rate',
  'Cost plus',
  'Hybrid',
  'Framework',
  'Service',
];

export const COMMERCIAL_EVALUATION_METHODS = [
  'Lowest evaluated cost',
  'Weighted technical-commercial',
  'Other preapproved method',
];

export function workflowStagesFor(kind: EpcInvitationKind | 'PQ'): readonly string[] {
  if (kind === 'EOI' || kind === 'PQ') return EOI_WORKFLOW_STAGES;
  if (kind === 'RFQ') return RFQ_WORKFLOW_STAGES;
  return RFT_WORKFLOW_STAGES;
}

function text(v: unknown): string {
  return v == null ? '' : String(v);
}

function num(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function bool(v: unknown, fallback = false): boolean {
  if (v == null || v === '') return fallback;
  return v === true || v === 1 || v === '1' || v === 'true';
}

export function emptyInvitationDossier(kind: EpcInvitationKind | 'PQ' = 'RFT'): EpcInvitationDossier {
  return {
    stage: workflowStagesFor(kind)[0],
    purpose: '',
    businessUnit: '',
    contractCategory: '',
    qualification: {
      certificates: '',
      experience: '',
      capacity: '',
      financial: '',
      hse: '',
      iso: '',
      localCompliance: '',
      criteriaFrozen: false,
    },
    ownership: {
      requestor: '',
      commercialOwner: '',
      technicalReviewer: '',
      approver: '',
    },
    commercialTerms: {
      paymentTerms: '',
      incoterms: '',
      freight: '',
      vat: '',
      warranty: '',
    },
    rft: {
      particulars: '',
      sow: '',
      boq: '',
      technicalSpecification: '',
      qualification: '',
      commercialConditions: '',
      contractConditions: '',
      evaluationMethodology: '',
      bidInstructions: '',
      experienceWeight: kind === 'RFT' ? 25 : null,
      methodologyWeight: kind === 'RFT' ? 30 : null,
      resourcesWeight: kind === 'RFT' ? 20 : null,
      hseWeight: kind === 'RFT' ? 15 : null,
      deliveryWeight: kind === 'RFT' ? 10 : null,
      qualificationThreshold: kind === 'RFT' ? 70 : null,
      commercialMethod: 'Weighted technical-commercial',
      commercialLockedUntilTechnical: kind === 'RFT',
    },
  };
}

export function parseInvitationDossier(meta: unknown, kind: EpcInvitationKind | 'PQ' = 'RFT'): EpcInvitationDossier {
  const base = emptyInvitationDossier(kind);
  if (!meta || typeof meta !== 'object') return base;
  const raw = (meta as { invitation_dossier?: unknown }).invitation_dossier;
  if (!raw || typeof raw !== 'object') return base;
  const d = raw as Record<string, unknown>;
  const q = (d.qualification || {}) as Record<string, unknown>;
  const o = (d.ownership || {}) as Record<string, unknown>;
  const c = (d.commercialTerms || {}) as Record<string, unknown>;
  const r = (d.rft || {}) as Record<string, unknown>;
  return {
    stage: text(d.stage) || base.stage,
    purpose: text(d.purpose),
    businessUnit: text(d.businessUnit),
    contractCategory: text(d.contractCategory),
    qualification: {
      certificates: text(q.certificates),
      experience: text(q.experience),
      capacity: text(q.capacity),
      financial: text(q.financial),
      hse: text(q.hse),
      iso: text(q.iso),
      localCompliance: text(q.localCompliance),
      criteriaFrozen: bool(q.criteriaFrozen),
    },
    ownership: {
      requestor: text(o.requestor),
      commercialOwner: text(o.commercialOwner),
      technicalReviewer: text(o.technicalReviewer),
      approver: text(o.approver),
    },
    commercialTerms: {
      paymentTerms: text(c.paymentTerms),
      incoterms: text(c.incoterms),
      freight: text(c.freight),
      vat: text(c.vat),
      warranty: text(c.warranty),
    },
    rft: {
      particulars: text(r.particulars),
      sow: text(r.sow),
      boq: text(r.boq),
      technicalSpecification: text(r.technicalSpecification),
      qualification: text(r.qualification),
      commercialConditions: text(r.commercialConditions),
      contractConditions: text(r.contractConditions),
      evaluationMethodology: text(r.evaluationMethodology),
      bidInstructions: text(r.bidInstructions),
      experienceWeight: num(r.experienceWeight) ?? base.rft.experienceWeight,
      methodologyWeight: num(r.methodologyWeight) ?? base.rft.methodologyWeight,
      resourcesWeight: num(r.resourcesWeight) ?? base.rft.resourcesWeight,
      hseWeight: num(r.hseWeight) ?? base.rft.hseWeight,
      deliveryWeight: num(r.deliveryWeight) ?? base.rft.deliveryWeight,
      qualificationThreshold: num(r.qualificationThreshold) ?? base.rft.qualificationThreshold,
      commercialMethod: text(r.commercialMethod) || base.rft.commercialMethod,
      commercialLockedUntilTechnical: bool(
        r.commercialLockedUntilTechnical,
        base.rft.commercialLockedUntilTechnical
      ),
    },
  };
}

export function mergeInvitationDossier(
  meta: unknown,
  dossier: EpcInvitationDossier
): Record<string, unknown> {
  const base = meta && typeof meta === 'object' ? { ...(meta as Record<string, unknown>) } : {};
  return { ...base, invitation_dossier: dossier };
}

export function technicalWeightTotal(rft: EpcRftDossier): number {
  return [rft.experienceWeight, rft.methodologyWeight, rft.resourcesWeight, rft.hseWeight, rft.deliveryWeight]
    .reduce<number>((sum, n) => sum + (n ?? 0), 0);
}
